/** @module @liquidefy/code-editor/plugins/plugins-sandbox @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  PluginId,
  PluginManifest,
  PluginState,
  PluginPermission,
  PluginResourceUsage,
  SandboxCapability,
  SandboxResourceLimits,
  SandboxConfig,
  MarketplaceEntry,
  MarketplaceSearchQuery,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asPluginId,
  now,
  DEFAULT_SANDBOX_RESOURCE_LIMITS,
} from '../code-editor.js';

// ═══ §2 SANDBOX CONFIGURATION — createSandboxConfig, defaults, restricted, trusted, merge ═══

export const DEFAULT_SANDBOX_CONFIG: SandboxConfig = {
  runtime: 'worker',
  permissions: [],
  resourceLimits: DEFAULT_SANDBOX_RESOURCE_LIMITS,
  capabilities: [],
};

export function createSandboxConfig(
  runtime: SandboxConfig['runtime'],
  permissions: PluginPermission[],
  overrides?: Partial<SandboxConfig>
): SandboxConfig {
  const capabilities: SandboxCapability[] = [];
  for (const perm of permissions) {
    const caps = capabilitiesForPermission(perm);
    for (const cap of caps) {
      if (!capabilities.includes(cap)) capabilities.push(cap);
    }
  }
  return {
    runtime: overrides?.runtime ?? runtime,
    permissions: overrides?.permissions ?? permissions,
    resourceLimits: overrides?.resourceLimits ?? DEFAULT_SANDBOX_RESOURCE_LIMITS,
    capabilities: overrides?.capabilities ?? capabilities,
  };
}

export function restrictedSandbox(): SandboxConfig {
  return {
    runtime: 'worker',
    permissions: [],
    resourceLimits: {
      maxCpuMs: 2000,
      maxMemoryMb: 64,
      maxStorageMb: 10,
      maxNetworkRequests: 0,
      maxApiCallsPerMinute: 10,
    },
    capabilities: [],
  };
}

export function trustedSandbox(): SandboxConfig {
  const allPermissions: PluginPermission[] = [
    'fs.read', 'fs.write', 'network', 'process', 'clipboard',
    'storage', 'ui.notifications', 'ui.webview', 'terminal', 'git', 'debug', 'ai',
  ];
  const allCapabilities: SandboxCapability[] = [
    'dom-access', 'file-read', 'file-write', 'network-fetch', 'process-spawn',
    'clipboard-read', 'clipboard-write', 'storage-local', 'storage-cloud',
    'terminal-create', 'git-read', 'git-write', 'debug-attach', 'ai-query',
  ];
  return {
    runtime: 'process',
    permissions: allPermissions,
    resourceLimits: {
      maxCpuMs: 60000,
      maxMemoryMb: 1024,
      maxStorageMb: 500,
      maxNetworkRequests: 1000,
      maxApiCallsPerMinute: 300,
    },
    capabilities: allCapabilities,
  };
}

export function mergeSandboxConfig(
  base: SandboxConfig,
  overrides: Partial<SandboxConfig>
): SandboxConfig {
  const mergedPermissions = overrides.permissions
    ? [...new Set([...base.permissions, ...overrides.permissions])]
    : base.permissions;
  const mergedCapabilities = overrides.capabilities
    ? [...new Set([...base.capabilities, ...overrides.capabilities])]
    : base.capabilities;
  return {
    runtime: overrides.runtime ?? base.runtime,
    permissions: mergedPermissions,
    resourceLimits: overrides.resourceLimits
      ? { ...base.resourceLimits, ...overrides.resourceLimits }
      : base.resourceLimits,
    capabilities: mergedCapabilities,
  };
}

// ═══ §3 CAPABILITY MANAGEMENT — hasCapability, grantCapability, revokeCapability, requiredCapabilities ═══

export const PERMISSION_CAPABILITY_MAP: Record<PluginPermission, SandboxCapability[]> = {
  'fs.read': ['file-read'],
  'fs.write': ['file-read', 'file-write'],
  'network': ['network-fetch'],
  'process': ['process-spawn'],
  'clipboard': ['clipboard-read', 'clipboard-write'],
  'storage': ['storage-local', 'storage-cloud'],
  'ui.notifications': ['dom-access'],
  'ui.webview': ['dom-access'],
  'terminal': ['terminal-create'],
  'git': ['git-read', 'git-write'],
  'debug': ['debug-attach'],
  'ai': ['ai-query'],
};

export function hasCapability(config: SandboxConfig, capability: SandboxCapability): boolean {
  return config.capabilities.includes(capability);
}

export function grantCapability(config: SandboxConfig, capability: SandboxCapability): SandboxConfig {
  if (config.capabilities.includes(capability)) return config;
  return { ...config, capabilities: [...config.capabilities, capability] };
}

export function revokeCapability(config: SandboxConfig, capability: SandboxCapability): SandboxConfig {
  return { ...config, capabilities: config.capabilities.filter(c => c !== capability) };
}

export function requiredCapabilities(manifest: PluginManifest): SandboxCapability[] {
  const caps = new Set<SandboxCapability>();
  for (const perm of manifest.permissions) {
    const permCaps = PERMISSION_CAPABILITY_MAP[perm];
    if (permCaps) {
      for (const c of permCaps) caps.add(c);
    }
  }
  if (manifest.contributes.views && manifest.contributes.views.length > 0) {
    caps.add('dom-access');
  }
  if (manifest.contributes.debuggers && manifest.contributes.debuggers.length > 0) {
    caps.add('debug-attach');
  }
  return [...caps];
}

export function capabilitiesForPermission(permission: PluginPermission): SandboxCapability[] {
  return PERMISSION_CAPABILITY_MAP[permission] ?? [];
}

const DANGEROUS_CAPABILITIES: ReadonlyArray<SandboxCapability> = [
  'process-spawn', 'file-write', 'network-fetch', 'git-write', 'debug-attach',
];

export function isCapabilityDangerous(capability: SandboxCapability): boolean {
  return DANGEROUS_CAPABILITIES.includes(capability);
}

export function dangerousCapabilities(config: SandboxConfig): SandboxCapability[] {
  return config.capabilities.filter(c => isCapabilityDangerous(c));
}

// ═══ §4 RESOURCE LIMITS — createResourceLimits, isWithinLimits, usagePercentage, shouldTerminate, scaleLimit ═══

export const RUNTIME_LIMITS: Record<SandboxConfig['runtime'], SandboxResourceLimits> = {
  worker: {
    maxCpuMs: 10000,
    maxMemoryMb: 256,
    maxStorageMb: 100,
    maxNetworkRequests: 100,
    maxApiCallsPerMinute: 60,
  },
  iframe: {
    maxCpuMs: 5000,
    maxMemoryMb: 128,
    maxStorageMb: 50,
    maxNetworkRequests: 50,
    maxApiCallsPerMinute: 30,
  },
  process: {
    maxCpuMs: 30000,
    maxMemoryMb: 512,
    maxStorageMb: 250,
    maxNetworkRequests: 500,
    maxApiCallsPerMinute: 120,
  },
  wasm: {
    maxCpuMs: 15000,
    maxMemoryMb: 384,
    maxStorageMb: 150,
    maxNetworkRequests: 200,
    maxApiCallsPerMinute: 90,
  },
};

export function createResourceLimits(overrides?: Partial<SandboxResourceLimits>): SandboxResourceLimits {
  return {
    maxCpuMs: overrides?.maxCpuMs ?? DEFAULT_SANDBOX_RESOURCE_LIMITS.maxCpuMs,
    maxMemoryMb: overrides?.maxMemoryMb ?? DEFAULT_SANDBOX_RESOURCE_LIMITS.maxMemoryMb,
    maxStorageMb: overrides?.maxStorageMb ?? DEFAULT_SANDBOX_RESOURCE_LIMITS.maxStorageMb,
    maxNetworkRequests: overrides?.maxNetworkRequests ?? DEFAULT_SANDBOX_RESOURCE_LIMITS.maxNetworkRequests,
    maxApiCallsPerMinute: overrides?.maxApiCallsPerMinute ?? DEFAULT_SANDBOX_RESOURCE_LIMITS.maxApiCallsPerMinute,
  };
}

export function isWithinLimits(usage: PluginResourceUsage, limits: SandboxResourceLimits): boolean {
  return (
    usage.cpuMs <= limits.maxCpuMs &&
    usage.memoryMb <= limits.maxMemoryMb &&
    usage.apiCalls <= limits.maxApiCallsPerMinute
  );
}

export function usagePercentage(
  usage: PluginResourceUsage,
  limits: SandboxResourceLimits
): { cpu: number; memory: number; apiCalls: number } {
  return {
    cpu: limits.maxCpuMs > 0 ? (usage.cpuMs / limits.maxCpuMs) * 100 : 0,
    memory: limits.maxMemoryMb > 0 ? (usage.memoryMb / limits.maxMemoryMb) * 100 : 0,
    apiCalls: limits.maxApiCallsPerMinute > 0 ? (usage.apiCalls / limits.maxApiCallsPerMinute) * 100 : 0,
  };
}

export function shouldTerminate(
  usage: PluginResourceUsage,
  limits: SandboxResourceLimits,
  gracePeriod: number = 1.1
): boolean {
  return (
    usage.cpuMs > limits.maxCpuMs * gracePeriod ||
    usage.memoryMb > limits.maxMemoryMb * gracePeriod ||
    usage.apiCalls > limits.maxApiCallsPerMinute * gracePeriod
  );
}

export function scaleLimit(limits: SandboxResourceLimits, factor: number): SandboxResourceLimits {
  return {
    maxCpuMs: Math.round(limits.maxCpuMs * factor),
    maxMemoryMb: Math.round(limits.maxMemoryMb * factor),
    maxStorageMb: Math.round(limits.maxStorageMb * factor),
    maxNetworkRequests: Math.round(limits.maxNetworkRequests * factor),
    maxApiCallsPerMinute: Math.round(limits.maxApiCallsPerMinute * factor),
  };
}

// ═══ §5 PERMISSION CHECKING — checkPermissions, missingPermissions, permissionDescription, groupPermissionsByRisk ═══

export const PERMISSION_DESCRIPTIONS: Record<PluginPermission, string> = {
  'fs.read': 'Read files from the local filesystem',
  'fs.write': 'Write files to the local filesystem',
  'network': 'Make network requests to external services',
  'process': 'Spawn child processes on the host system',
  'clipboard': 'Read and write system clipboard contents',
  'storage': 'Persist data in local or cloud storage',
  'ui.notifications': 'Display notifications to the user',
  'ui.webview': 'Render custom web-based views',
  'terminal': 'Create and interact with terminal sessions',
  'git': 'Read and write to git repositories',
  'debug': 'Attach to running debug sessions',
  'ai': 'Make queries to AI model providers',
};

export const PERMISSION_HIERARCHY: Record<string, PluginPermission[]> = {
  'filesystem': ['fs.read', 'fs.write'],
  'system': ['process', 'terminal'],
  'data': ['clipboard', 'storage'],
  'ui': ['ui.notifications', 'ui.webview'],
  'dev': ['git', 'debug'],
  'intelligence': ['ai'],
  'connectivity': ['network'],
};

export function checkPermissions(
  required: ReadonlyArray<PluginPermission>,
  granted: ReadonlyArray<PluginPermission>
): boolean {
  const grantedSet = new Set(granted);
  return required.every(r => grantedSet.has(r));
}

export function missingPermissions(
  required: ReadonlyArray<PluginPermission>,
  granted: ReadonlyArray<PluginPermission>
): PluginPermission[] {
  const grantedSet = new Set(granted);
  return required.filter(r => !grantedSet.has(r));
}

export function permissionDescription(permission: PluginPermission): string {
  return PERMISSION_DESCRIPTIONS[permission] ?? `Unknown permission: ${permission}`;
}

export function permissionRiskLevel(permission: PluginPermission): 'low' | 'medium' | 'high' {
  const high: PluginPermission[] = ['process', 'fs.write', 'network'];
  const medium: PluginPermission[] = ['terminal', 'git', 'debug', 'clipboard', 'ai'];
  if (high.includes(permission)) return 'high';
  if (medium.includes(permission)) return 'medium';
  return 'low';
}

export function groupPermissionsByRisk(
  permissions: ReadonlyArray<PluginPermission>
): { low: PluginPermission[]; medium: PluginPermission[]; high: PluginPermission[] } {
  const result = { low: [] as PluginPermission[], medium: [] as PluginPermission[], high: [] as PluginPermission[] };
  for (const perm of permissions) {
    const level = permissionRiskLevel(perm);
    result[level].push(perm);
  }
  return result;
}

export function isPermissionEscalation(
  current: ReadonlyArray<PluginPermission>,
  requested: ReadonlyArray<PluginPermission>
): boolean {
  const currentSet = new Set(current);
  for (const perm of requested) {
    if (!currentSet.has(perm)) return true;
  }
  return false;
}

// ═══ §6 MARKETPLACE — createMarketplaceEntry, searchMarketplace, filterByCategory, sortEntries ═══

export const DEFAULT_CATEGORIES: ReadonlyArray<string> = [
  'Languages',
  'Themes',
  'Snippets',
  'Formatters',
  'Linters',
  'Debuggers',
  'Testing',
  'Version Control',
  'AI',
  'Productivity',
  'Visualization',
  'Data',
  'DevOps',
  'Education',
  'Other',
];

export function createMarketplaceEntry(
  plugin: PluginState,
  metadata: {
    publisher: string;
    downloads: number;
    rating: number;
    reviewCount: number;
    categories: string[];
    tags: string[];
    verified: boolean;
  }
): MarketplaceEntry {
  return {
    pluginId: plugin.id,
    name: plugin.manifest.name,
    displayName: plugin.manifest.displayName,
    description: plugin.manifest.description,
    version: plugin.manifest.version,
    author: plugin.manifest.author,
    publisher: metadata.publisher,
    downloads: metadata.downloads,
    rating: metadata.rating,
    reviewCount: metadata.reviewCount,
    categories: metadata.categories,
    tags: metadata.tags,
    lastUpdated: now(),
    verified: metadata.verified,
  };
}

export function matchesQuery(entry: MarketplaceEntry, query: string): boolean {
  const lower = query.toLowerCase();
  return (
    entry.name.toLowerCase().includes(lower) ||
    entry.displayName.toLowerCase().includes(lower) ||
    entry.description.toLowerCase().includes(lower) ||
    entry.tags.some(t => t.toLowerCase().includes(lower)) ||
    entry.author.toLowerCase().includes(lower) ||
    entry.publisher.toLowerCase().includes(lower)
  );
}

export function searchMarketplace(
  entries: ReadonlyArray<MarketplaceEntry>,
  query: string
): MarketplaceEntry[] {
  if (!query.trim()) return [...entries];
  const results = entries.filter(e => matchesQuery(e, query));
  return results.sort((a, b) => entryScore(b) - entryScore(a));
}

export function filterByCategory(
  entries: ReadonlyArray<MarketplaceEntry>,
  category: string
): MarketplaceEntry[] {
  return entries.filter(e => e.categories.includes(category));
}

export function sortEntries(
  entries: ReadonlyArray<MarketplaceEntry>,
  by: 'relevance' | 'installs' | 'rating' | 'updated'
): MarketplaceEntry[] {
  const sorted = [...entries];
  switch (by) {
    case 'installs':
      return sorted.sort((a, b) => b.downloads - a.downloads);
    case 'rating':
      return sorted.sort((a, b) => b.rating - a.rating);
    case 'updated':
      return sorted.sort((a, b) => b.lastUpdated - a.lastUpdated);
    case 'relevance':
    default:
      return sorted.sort((a, b) => entryScore(b) - entryScore(a));
  }
}

export function isVerified(entry: MarketplaceEntry): boolean {
  return entry.verified;
}

export function isPopular(entry: MarketplaceEntry, threshold: number = 10000): boolean {
  return entry.downloads >= threshold;
}

export function isRecent(entry: MarketplaceEntry, maxAge: number = 30 * 24 * 60 * 60 * 1000): boolean {
  return (now() - entry.lastUpdated) < maxAge;
}

export function entryScore(entry: MarketplaceEntry): number {
  const downloadScore = Math.log10(Math.max(entry.downloads, 1)) * 10;
  const ratingScore = entry.rating * 20;
  const recencyDays = (now() - entry.lastUpdated) / (24 * 60 * 60 * 1000);
  const recencyScore = Math.max(0, 50 - recencyDays);
  const verifiedBonus = entry.verified ? 15 : 0;
  const reviewBonus = Math.min(entry.reviewCount, 100) * 0.1;
  return downloadScore + ratingScore + recencyScore + verifiedBonus + reviewBonus;
}
